import { useEffect, useMemo } from 'react';

import { TimezoneEntity } from 'api/types/entity';
import { useField } from 'libs/form/fields/useField';
import { useTranslation } from 'libs/i18n';
import { Stack, Typography } from 'libs/ui';
import { SettingsContent } from 'modules/settings/common/components';

import {
  Field,
  useFormContext,
  TimezoneFormType,
} from '../../forms/timezoneForm';
import { getTimezoneLabel } from '../../helpers/getTimezoneLabel';

export interface TimezoneSettingsProps {
  timezones: TimezoneEntity[];
}

export const TimezoneSettings = ({ timezones }: TimezoneSettingsProps) => {
  const { t } = useTranslation();
  const { values, initialValues } = useFormContext();

  const { helpers } = useField<TimezoneFormType['timezone']>('timezone');

  const timezonesOptions = useMemo(
    () =>
      timezones?.map((v) => ({
        value: v.name,
        label: getTimezoneLabel(v),
      })),
    [timezones]
  );

  useEffect(() => {
    if (values.isAutomaticTimezone) {
      helpers.setValue(initialValues.timezone);
    }
  }, [helpers, initialValues.timezone, values.isAutomaticTimezone]);

  return (
    <SettingsContent
      leftContent={
        <Stack alignSelf="center" justifyContent="space-between" spacing={8}>
          <Typography variant="bodyBold">
            {t('settings.timezone.title')}
          </Typography>
          <Stack alignItems="center" direction="row" spacing={16}>
            <Typography variant="bodyMedium">
              {t('settings.timezone.switchLabel')}
            </Typography>
            <Field.Switch name="isAutomaticTimezone" />
          </Stack>
        </Stack>
      }
      rightContent={
        <Field.Autocomplete
          helperTextAbsolute
          disabled={values.isAutomaticTimezone}
          label={t('settings.timezone.timezoneField.label')}
          name="timezone"
          optionLabelKey="label"
          options={timezonesOptions ?? []}
          optionValueKey="value"
          placeholder={t('settings.timezone.timezoneField.placeholder')}
          readOnly={values.isAutomaticTimezone}
        />
      }
    />
  );
};
